/**
 * Hook for OptiDVI (Digital Vehicle Inspection) feature gating and permissions
 * 
 * OptiDVI is an addon-gated feature that allows:
 * - Technicians: Upload media + trigger AI analysis
 * - Writers/Managers: Approve findings + add jobs to ticket
 */

import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';

export interface UseOptiDviResult {
  /** Whether the OPTIDVI addon is enabled for the current shop */
  isEnabled: boolean;
  /** Can upload DVI media (tech/writer/manager) */
  canUpload: boolean;
  /** Can trigger AI analysis (tech/writer/manager) */
  canAnalyze: boolean;
  /** Can approve/reject findings (writer/manager only) */
  canApprove: boolean;
  /** Can add recommended jobs to ticket (writer/manager only) */
  canAddToTicket: boolean;
  /** Can delete media/findings (writer/manager only) */
  canDelete: boolean;
}

export function useOptiDvi(): UseOptiDviResult {
  const { activeShop } = useAuth();
  const permissions = usePermissions();
  
  const isEnabled = activeShop?.addons?.includes('OPTIDVI') ?? false;
  
  return {
    isEnabled,
    // Techs can upload + trigger analysis
    canUpload: isEnabled && (permissions.isManager || permissions.isOwner || permissions.isServiceWriter || permissions.isTechnician),
    canAnalyze: isEnabled && (permissions.isManager || permissions.isOwner || permissions.isServiceWriter || permissions.isTechnician),
    // Only writers/managers can approve + add jobs
    canApprove: isEnabled && (permissions.isManager || permissions.isOwner || permissions.isServiceWriter),
    canAddToTicket: isEnabled && (permissions.isManager || permissions.isOwner || permissions.isServiceWriter),
    canDelete: isEnabled && (permissions.isManager || permissions.isOwner || permissions.isServiceWriter),
  };
}
